/**
 * Pure view model for the admin usage page (ATRU-06; COP-134 D4).
 *
 * Everything the page formats lives here, not in the component: the request
 * window, the chart `{columns, rows}` handed to `DbChartECharts`, the exact-
 * count table rows, and every sentence the page states about the numbers.
 * That split is what makes the wording testable — `Usage.tsx` boots a
 * `HubClient` at module load, so a unit test can never import it.
 *
 * Honesty rules this module encodes (the page exists to avoid a misleading
 * count, so none of these are decorative):
 *  - `total` is DIMENSION-SCOPED. The hub excludes rows whose queried
 *    dimension is NULL from both `rows` and `total`, so the same window can
 *    report a smaller total for `groupBy=user` than for `groupBy=route`. The
 *    headline sentence therefore NAMES its dimension — a bare number would let
 *    an operator switch the selector, watch the number move, and conclude the
 *    page is broken.
 *  - The retention horizon is always stated, and a window that reaches past it
 *    says so — a count without its horizon under-reports silently (HUB-064).
 *  - A capped row list says so, because the rows then do not sum to `total`.
 *
 * Dependency-light on purpose: `@/lib/format` only (same rule that module
 * states about itself). No `@/lib/hub-client`, direct or transitive.
 */
import type {
  DbTablePartColumn,
  UsageSummaryGroupBy,
  UsageSummaryResponse,
} from '@verybigthings/trackframe-shared/schemas';
import { fmtDate } from '@/lib/format';

/** The only window presets the page offers (COP-134 D4 — no free-date picker). */
export const USAGE_WINDOW_PRESETS = [7, 30, 90] as const;
export type UsageWindowDays = (typeof USAGE_WINDOW_PRESETS)[number];

/** Matches the hub's own default window (`USAGE_SUMMARY_DEFAULT_WINDOW_DAYS`). */
export const USAGE_DEFAULT_WINDOW_DAYS: UsageWindowDays = 30;

/** The three dimensions `GET /usage/summary` aggregates on. */
export const USAGE_GROUP_BY_OPTIONS: readonly UsageSummaryGroupBy[] = [
  'track',
  'user',
  'route',
] as const;

/** Human label per dimension — used by the selector AND the table's first
 *  column header, so the two can never disagree. */
export const USAGE_GROUP_BY_LABELS: Record<UsageSummaryGroupBy, string> = {
  track: 'Track',
  user: 'User',
  route: 'Route',
};

const DAY_MS = 86_400_000;

export const USAGE_EMPTY_COPY = 'No recorded activity in this window.';

export const USAGE_DENIED_COPY =
  'You do not have access to usage. This page is admin-only.';

export const USAGE_ERROR_PREFIX = 'Could not load usage';

/** Thousands separators without an ICU dependency — the pinned strings must be
 *  identical in every environment the suite runs in. */
export function formatCount(value: number): string {
  return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * The `[from, to)` window + dimension for one request. `to` is `now`, `from` is
 * `now - days`. Half-open by the endpoint's contract; `now` is injectable so
 * the pin is deterministic.
 */
export function buildUsageQuery(
  days: number,
  groupBy: UsageSummaryGroupBy,
  now: Date = new Date(),
): { from: string; to: string; groupBy: UsageSummaryGroupBy } {
  return {
    from: new Date(now.getTime() - days * DAY_MS).toISOString(),
    to: now.toISOString(),
    groupBy,
  };
}

/**
 * The chart's synthetic column set. `buildChartOption('bar', …)` takes the
 * first `'string'` dimension as the x axis and every `'metric'` column as a
 * series, and it names the series from the LAST path segment — hence
 * `usage.Requests`, which is what the legend reads.
 */
export const USAGE_CHART_COLUMNS: DbTablePartColumn[] = [
  { key: 'label', path: 'usage.label', type: 'string', memberType: 'dimension' },
  { key: 'count', path: 'usage.Requests', type: 'number', memberType: 'metric' },
];

export interface UsageChartModel {
  columns: DbTablePartColumn[];
  rows: Array<{ label: string; count: number }>;
}

/**
 * Map the response into `DbChartEChartsProps`-shaped data. The server already
 * orders by `count DESC, key ASC`; the mapping is a pass-through, never a
 * client-side re-sort (a second opinion about order is a second truth).
 */
export function buildUsageChart(response: UsageSummaryResponse): UsageChartModel {
  return {
    columns: USAGE_CHART_COLUMNS,
    rows: response.rows.map((row) => ({ label: row.label, count: row.count })),
  };
}

export interface UsageTableRow {
  /** Raw dimension value (`trackId` / `actorUserId` / `httpRoute`). */
  key: string;
  label: string;
  countText: string;
}

/** Exact counts in the server's order — what an operator copies into an update. */
export function buildUsageTableRows(response: UsageSummaryResponse): UsageTableRow[] {
  return response.rows.map((row) => ({
    key: row.key,
    label: row.label,
    countText: formatCount(row.count),
  }));
}

/**
 * The headline sentence. It NAMES the dimension because `total` counts only the
 * events attributable to that dimension (COP-134 D4 — task-05 drops NULL-
 * dimension rows from `rows` AND `total`).
 */
export function usageTotalSentence(response: UsageSummaryResponse): string {
  const noun = response.total === 1 ? 'request' : 'requests';
  return `${formatCount(response.total)} ${noun} attributed to a ${response.groupBy}`;
}

/** `2026-05-13 → 2026-06-12` — the window the numbers describe. */
export function usageWindowSentence(response: UsageSummaryResponse): string {
  return `${fmtDate(response.from)} → ${fmtDate(response.to)}`;
}

/** The retention horizon in effect. Always rendered; `0` means unlimited. */
export function usageRetentionSentence(response: UsageSummaryResponse): string {
  if (response.retentionDays === 0) return 'Audit events are retained indefinitely.';
  const noun = response.retentionDays === 1 ? 'day' : 'days';
  return `Audit events are retained for ${response.retentionDays} ${noun}.`;
}

/**
 * The horizon warning — only when the window reaches past retention, i.e. the
 * HUB-064 sweeper has already deleted rows this window asked for.
 */
export function usageRetentionWarning(response: UsageSummaryResponse): string | null {
  if (!response.truncatedByRetention) return null;
  // `30-day` — an attributive compound stays singular whatever the number is.
  return `This window starts before the ${response.retentionDays}-day audit retention horizon, so the counts under-report — the older events have already been deleted.`;
}

/**
 * The row-cap note — only when the list was cut. Without it the table's rows do
 * not sum to the headline total and nothing on screen says why.
 */
export function usageRowCapNote(response: UsageSummaryResponse): string | null {
  if (!response.truncated) return null;
  return `Showing the top ${response.rows.length} by count; the total above covers the whole window.`;
}

/** House error copy for a failed load. 403 is the admin gate answering. */
export function usageErrorCopy(httpStatus: number | null, message: string): string {
  if (httpStatus === 403) return USAGE_DENIED_COPY;
  return `${USAGE_ERROR_PREFIX}: ${message}`;
}
